'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { api } from '@/lib/api'
import { ArrowLeft, CheckCircle, Camera, X } from 'lucide-react'
import Link from 'next/link'

type Step = 'barcode' | 'info' | 'prix' | 'stock' | 'done'

// ─── Barcode Scanner (ZXing) ─────────────────────────────────────────────────
function BarcodeScanner({ onDetected, onClose }: { onDetected: (code: string) => void; onClose: () => void }) {
  const [error, setError] = useState('')
  const [debug, setDebug] = useState('Démarrage caméra...')
  const [busy, setBusy] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const readerRef = useRef<any>(null)
  const controlsRef = useRef<any>(null)
  const stoppedRef = useRef(false)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    start()
    return () => {
      stoppedRef.current = true
      try { controlsRef.current?.stop() } catch {}
      streamRef.current?.getTracks().forEach(t => t.stop())
    }
  }, [])

  async function buildReader() {
    const { BrowserMultiFormatReader } = await import('@zxing/browser')
    const { DecodeHintType, BarcodeFormat } = await import('@zxing/library')
    const hints = new Map()
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93,
      BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E, BarcodeFormat.ITF, BarcodeFormat.CODABAR,
      BarcodeFormat.QR_CODE,
    ])
    hints.set(DecodeHintType.TRY_HARDER, true)
    return new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 } as any)
  }

  async function start() {
    try {
      const reader = await buildReader()
      readerRef.current = reader
      setDebug('Scan en cours — pointe le barcode')
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } },
        videoRef.current!,
        (result: any) => {
          if (result && !stoppedRef.current) {
            stoppedRef.current = true
            try { controlsRef.current?.stop() } catch {}
            onDetected(result.getText())
          }
        }
      )
      controlsRef.current = controls
      // garder une réf au stream pour la torche
      streamRef.current = (videoRef.current?.srcObject as MediaStream) || null
    } catch (e: any) {
      setError(e?.message?.toLowerCase().includes('ermission') ? 'Accès caméra refusé.' : 'Erreur: ' + (e?.message || ''))
    }
  }

  // 1) Capture depuis le flux vidéo live (canvas)
  async function captureFromVideo() {
    const video = videoRef.current
    if (!video || busy || !video.videoWidth) { setDebug('Caméra pas prête...'); return }
    setBusy(true)
    setDebug('Analyse...')
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d')!.drawImage(video, 0, 0)
      const reader = readerRef.current || await buildReader()
      try {
        const result = reader.decodeFromCanvas(canvas)
        finish(result.getText()); return
      } catch {
        setDebug('Pas trouvé. Essaie "Prendre une photo" (meilleure qualité).')
      }
    } catch (e: any) {
      setDebug('Erreur: ' + (e?.message || e))
    } finally {
      setBusy(false)
    }
  }

  // 2) Photo native → décode avec plusieurs rotations + crop (le plus robuste)
  async function decodeImageFile(file: File) {
    setBusy(true)
    setDebug(`Analyse... (${Math.round(file.size / 1024)} Ko)`)
    const url = URL.createObjectURL(file)
    try {
      const reader = readerRef.current || await buildReader()
      const img = new Image()
      img.src = url
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej })

      const W = img.naturalWidth, H = img.naturalHeight
      const maxSide = 1600

      // Dessine l'image avec rotation (deg) + crop optionnel (fraction centrale), retourne canvas
      function render(deg: number, cropFrac: number): HTMLCanvasElement {
        const rot = deg % 360
        const swap = rot === 90 || rot === 270
        const baseW = swap ? H : W
        const baseH = swap ? W : H
        const scale = Math.min(1, maxSide / Math.max(baseW, baseH))
        const cw = Math.round(baseW * scale)
        const ch = Math.round(baseH * scale)
        const canvas = document.createElement('canvas')
        canvas.width = cw; canvas.height = ch
        const ctx = canvas.getContext('2d')!
        ctx.save()
        ctx.translate(cw / 2, ch / 2)
        ctx.rotate((rot * Math.PI) / 180)
        const dw = (swap ? ch : cw)
        const dh = (swap ? cw : ch)
        ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh)
        ctx.restore()
        if (cropFrac >= 1) return canvas
        // crop bande centrale verticale
        const bandH = Math.round(ch * cropFrac)
        const y = Math.round((ch - bandH) / 2)
        const band = document.createElement('canvas')
        band.width = cw; band.height = bandH
        band.getContext('2d')!.drawImage(canvas, 0, y, cw, bandH, 0, 0, cw, bandH)
        return band
      }

      // Essais: (rotation, crop). 0° et 90° couvrent les barcodes horizontaux/verticaux.
      const attempts: Array<[number, number]> = [
        [0, 1], [90, 1], [0, 0.4], [90, 0.4], [180, 1], [270, 1],
      ]
      async function decodeCanvas(c: HTMLCanvasElement): Promise<string> {
        const dataUrl = c.toDataURL('image/png')
        const im = new Image()
        im.src = dataUrl
        await new Promise((res, rej) => { im.onload = res; im.onerror = rej })
        const r = await reader.decodeFromImageElement(im)
        return r.getText()
      }

      for (const [deg, crop] of attempts) {
        setDebug(`Analyse... rotation ${deg}° crop ${crop}`)
        try {
          const text = await decodeCanvas(render(deg, crop))
          finish(text); return
        } catch { /* essai suivant */ }
        await new Promise(r => setTimeout(r, 0))
      }
      setDebug(`Pas trouvé [${W}×${H}]. Cadre le barcode bien droit, net, qui remplit la largeur.`)
    } catch (e: any) {
      setDebug('Erreur: ' + (e?.name || e?.message || e))
    } finally {
      URL.revokeObjectURL(url)
      setBusy(false)
    }
  }

  function finish(code: string) {
    stoppedRef.current = true
    try { controlsRef.current?.stop() } catch {}
    streamRef.current?.getTracks().forEach(t => t.stop())
    onDetected(code)
  }

  async function toggleTorch() {
    const track = (videoRef.current?.srcObject as MediaStream)?.getVideoTracks()[0]
    if (track) {
      try { await track.applyConstraints({ advanced: [{ torch: true } as any] }) } catch {}
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 pt-12 pb-3">
        <button onClick={onClose} className="text-white p-2"><X className="w-6 h-6" /></button>
        <p className="text-white font-semibold">Scanner le barcode</p>
        <button onClick={toggleTorch} className="p-2 text-white">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M7 2v11h3v9l7-12h-4l4-8z" /></svg>
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden bg-black">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
        {/* Viewfinder overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-72 h-28">
            {[['top-0 left-0','border-t-4 border-l-4 rounded-tl-lg'],
              ['top-0 right-0','border-t-4 border-r-4 rounded-tr-lg'],
              ['bottom-0 left-0','border-b-4 border-l-4 rounded-bl-lg'],
              ['bottom-0 right-0','border-b-4 border-r-4 rounded-br-lg'],
            ].map(([pos, cls]) => (
              <div key={pos} className={`absolute w-8 h-8 border-indigo-400 ${pos} ${cls}`} />
            ))}
            <div className="absolute left-2 right-2 top-1/2 h-0.5 bg-indigo-400"
              style={{ boxShadow: '0 0 10px #818cf8, 0 0 20px #818cf8' }} />
          </div>
        </div>
        {error && (
          <div className="absolute bottom-8 left-0 right-0 mx-6 bg-red-500/90 text-white text-center py-3 rounded-2xl text-sm font-semibold">
            {error}
          </div>
        )}
      </div>

      <div className="px-6 py-5 space-y-3">
        <p className="text-white/50 text-xs text-center font-mono min-h-[16px]">{debug}</p>
        <div className="flex gap-3">
          <button onClick={captureFromVideo} disabled={busy}
            className="flex-1 py-4 bg-white/10 border border-white/30 text-white rounded-2xl font-semibold active:scale-95 disabled:opacity-50">
            Capturer
          </button>
          <label className={`flex-1 py-4 bg-white text-gray-900 rounded-2xl font-bold active:scale-95 flex items-center justify-center gap-2 cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
            {busy
              ? <span className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
              : <><Camera className="w-5 h-5" /> Photo</>}
            <input type="file" accept="image/*" capture="environment" className="hidden"
              onChange={e => e.target.files?.[0] && decodeImageFile(e.target.files[0])} />
          </label>
        </div>
        <p className="text-white/40 text-xs text-center">"Photo" = autofocus + haute qualité (recommandé pour Code128)</p>
      </div>
    </div>
  )
}

// Resize image to maxPx (longest side), returns base64 dataURL
async function resizeImage(file: File, maxPx: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const c = document.createElement('canvas')
      c.width = w; c.height = h
      c.getContext('2d')!.drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(url)
      resolve(c.toDataURL('image/jpeg', 0.9))
    }
    img.onerror = reject
    img.src = url
  })
}

// ─── OCR Scanner (texte / référence) ─────────────────────────────────────────
function OcrScanner({ onDetected, onClose }: { onDetected: (code: string) => void; onClose: () => void }) {
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const [tokens, setTokens] = useState<string[]>([])
  const [fullText, setFullText] = useState('')
  const [preview, setPreview] = useState<string | null>(null)

  async function runOcr(file: File) {
    setBusy(true); setTokens([]); setFullText(''); setProgress(0)
    try {
      // Resize image to max 1200px before OCR — évite freeze sur photos 12MP
      const resized = await resizeImage(file, 1200)
      setPreview(resized)

      const Tesseract = (await import('tesseract.js')).default
      const { data } = await Tesseract.recognize(resized, 'eng', {
        logger: (m: any) => { if (m.status === 'recognizing text') setProgress(Math.round(m.progress * 100)) },
      })
      const text: string = data.text || ''
      setFullText(text)
      // Candidats: tokens alphanumériques (majuscules+chiffres), longueur >= 4
      const raw = text.toUpperCase().match(/[A-Z0-9][A-Z0-9\-/]{3,}/g) || []
      const uniq = Array.from(new Set(raw.map(t => t.replace(/[^A-Z0-9\-/]/g, '')).filter(t => t.length >= 4)))
      // Priorité: ceux qui mélangent lettres ET chiffres (réfs modèles)
      uniq.sort((a, b) => {
        const score = (s: string) => (/[A-Z]/.test(s) && /[0-9]/.test(s) ? 2 : 1) * s.length
        return score(b) - score(a)
      })
      setTokens(uniq.slice(0, 12))
    } catch (e: any) {
      setFullText('Erreur OCR: ' + (e?.message || e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between px-4 pt-12 pb-3">
        <button onClick={onClose} className="text-white p-2"><X className="w-6 h-6" /></button>
        <p className="text-white font-semibold">Scanner le texte (OCR)</p>
        <span className="w-10" />
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {!preview && (
          <label className="block w-full py-16 rounded-3xl border-2 border-dashed border-white/30 bg-white/5 text-center cursor-pointer active:scale-95">
            <Camera className="w-12 h-12 text-white/70 mx-auto mb-3" />
            <p className="text-white font-bold text-lg">Prendre une photo de l'étiquette</p>
            <p className="text-white/50 text-sm mt-1">Cadre bien la référence (ex: CRDN570ZX)</p>
            <input type="file" accept="image/*" capture="environment" className="hidden"
              onChange={e => e.target.files?.[0] && runOcr(e.target.files[0])} />
          </label>
        )}

        {preview && (
          <div className="space-y-4">
            <img src={preview} alt="" className="w-full max-h-52 object-contain rounded-2xl bg-black" />

            {busy && (
              <div className="text-center py-4">
                <div className="w-10 h-10 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-white/70 text-sm mt-3">Lecture du texte... {progress}%</p>
              </div>
            )}

            {!busy && tokens.length > 0 && (
              <div>
                <p className="text-white/70 text-sm mb-2 font-semibold">Touche la bonne référence :</p>
                <div className="flex flex-wrap gap-2">
                  {tokens.map(t => (
                    <button key={t} onClick={() => onDetected(t)}
                      className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-mono font-bold active:scale-95">
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!busy && tokens.length === 0 && fullText && (
              <p className="text-amber-300 text-sm text-center">Aucune référence claire trouvée. Reprends une photo plus nette.</p>
            )}

            {!busy && (
              <label className="block w-full py-3.5 rounded-2xl border border-white/30 text-white text-center font-semibold cursor-pointer active:bg-white/10">
                Reprendre une photo
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => e.target.files?.[0] && runOcr(e.target.files[0])} />
              </label>
            )}

            {!busy && fullText && !fullText.startsWith('Erreur') && (
              <details className="text-white/50 text-xs">
                <summary className="cursor-pointer">Voir tout le texte lu</summary>
                <pre className="whitespace-pre-wrap mt-2 bg-black/30 p-3 rounded-xl">{fullText}</pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function NewArticlePage() {
  const { session } = useApp()
  const router = useRouter()

  const [step, setStep] = useState<Step>('barcode')
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [showScanner, setShowScanner] = useState(false)
  const [showOcr, setShowOcr] = useState(false)
  const [checking, setChecking] = useState(false)
  const [manualMode, setManualMode] = useState(false)
  const [scannedRaw, setScannedRaw] = useState<string | null>(null) // valeur scannée avant confirmation
  const [form, setForm] = useState({
    name: '', barcode: '', category_id: '', unit: 'pièce',
    ras_mel: '', prix_vente: '', prix_min: '', stock: '', stock_alert: '5'
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    fetch(`/api/categories?shop_id=${session.shop.id}`)
      .then(r => r.json()).then(setCategories).catch(() => setCategories([]))
  }, [session])

  function set(key: string, val: string) {
    setForm(f => ({ ...f, [key]: val }))
    setErrors(e => ({ ...e, [key]: '' }))
  }

  // After scanning or typing barcode → check if exists
  async function checkBarcode(code: string) {
    if (!code.trim()) { setErrors({ barcode: 'Barcode obligatoire' }); return }
    setChecking(true)
    const res = await fetch(`/api/articles?shop_id=${session!.shop.id}&search=${encodeURIComponent(code.trim())}`)
    const articles = await res.json()
    setChecking(false)
    // Exact match on barcode
    const existing = articles.find((a: any) => a.barcode === code.trim())
    if (existing) {
      // Article exists → go edit it
      router.push(`/articles/${existing.id}?from=scan`)
    } else {
      set('barcode', code.trim())
      setStep('info')
    }
  }

  function validate() {
    const e: Record<string, string> = {}
    if (step === 'info' && !form.name.trim()) e.name = 'Nom obligatoire'
    if (step === 'prix') {
      if (!form.ras_mel) e.ras_mel = 'Obligatoire'
      if (!form.prix_vente) e.prix_vente = 'Obligatoire'
      if (!form.prix_min) e.prix_min = 'Obligatoire'
      if (form.prix_min && form.prix_vente && parseFloat(form.prix_min) > parseFloat(form.prix_vente))
        e.prix_min = 'Prix min > prix vente!'
    }
    if (step === 'stock' && !form.stock) e.stock = 'Obligatoire'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function next() {
    if (!validate()) return
    const steps: Step[] = ['info', 'prix', 'stock', 'done']
    const idx = steps.indexOf(step)
    if (steps[idx + 1] === 'done') {
      setLoading(true)
      await api.createArticle({
        shop_id: session!.shop.id,
        name: form.name.trim(),
        barcode: form.barcode || null,
        category_id: form.category_id || null,
        unit: form.unit,
        ras_mel: parseFloat(form.ras_mel),
        prix_vente: parseFloat(form.prix_vente),
        prix_min: parseFloat(form.prix_min),
        stock: parseInt(form.stock),
        stock_alert: parseInt(form.stock_alert)
      })
      setLoading(false)
    }
    setStep(steps[idx + 1])
  }

  const stepNum: Record<Step, number> = { barcode: 0, info: 1, prix: 2, stock: 3, done: 4 }
  const stepLabels = [{ s: 'info', label: '1. Article' }, { s: 'prix', label: '2. Prix' }, { s: 'stock', label: '3. Stock' }]

  return (
    <div className="min-h-screen pb-6">
      {showScanner && (
        <BarcodeScanner
          onDetected={code => {
            setShowScanner(false)
            setScannedRaw(code)
            set('barcode', code)
            setManualMode(true)
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {showOcr && (
        <OcrScanner
          onDetected={code => {
            setShowOcr(false)
            setScannedRaw(code)
            set('barcode', code)
            setManualMode(true)
          }}
          onClose={() => setShowOcr(false)}
        />
      )}

      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/articles" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900">Nouvel article</h1>
        </div>
        {step !== 'barcode' && step !== 'done' && (
          <div className="flex px-4 pb-4 gap-2">
            {stepLabels.map(({ s, label }, i) => (
              <div key={s} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                  ${s === step ? 'bg-indigo-600 text-white' : stepNum[s as Step] < stepNum[step] ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {i + 1}
                </div>
                <span className="text-xs text-gray-500">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pt-6">

        {/* ── STEP 0: BARCODE ── */}
        {step === 'barcode' && (
          <div className="space-y-5">
            <div className="text-center pb-2">
              <p className="text-gray-500 text-sm">Scanner le barcode de l'article pour commencer</p>
            </div>

            {/* Scan button */}
            {!manualMode && (
              <>
                <button onClick={() => setShowScanner(true)}
                  className="w-full py-12 rounded-3xl border-2 border-dashed border-indigo-300 bg-indigo-50 flex flex-col items-center gap-3 active:scale-95 transition-transform">
                  <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center">
                    <Camera className="w-8 h-8 text-white" />
                  </div>
                  <span className="text-indigo-700 font-bold text-lg">Scanner le barcode</span>
                  <span className="text-indigo-400 text-sm">Pour les vrais codes-barres produits</span>
                </button>

                <button onClick={() => setShowOcr(true)}
                  className="w-full py-4 rounded-2xl border-2 border-emerald-300 bg-emerald-50 flex items-center justify-center gap-2 text-emerald-700 font-bold active:scale-95">
                  <Camera className="w-5 h-5" /> Scanner le texte (OCR)
                  <span className="text-emerald-500 text-xs font-normal">ref: CRDN570ZX…</span>
                </button>
              </>
            )}

            {/* Manual input */}
            <div>
              {manualMode ? (
                <>
                  {scannedRaw && (
                    <div className="mb-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                      <p className="text-amber-700 text-xs mb-1">📷 Barcode scanné: <span className="font-mono font-bold">{scannedRaw}</span></p>
                      <p className="text-amber-600 text-xs">Modifie si tu veux utiliser la ref modèle (ex: CRDN570ZX)</p>
                    </div>
                  )}
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Référence *</label>
                  <input
                    autoFocus
                    value={form.barcode}
                    onChange={e => set('barcode', e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && checkBarcode(form.barcode)}
                    placeholder="Ex: CRDN570ZX"
                    className={`w-full px-4 py-4 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 text-lg font-mono ${errors.barcode ? 'border-red-400' : 'border-gray-200'}`}
                  />
                  {errors.barcode && <p className="text-red-500 text-sm mt-1">{errors.barcode}</p>}
                </>
              ) : (
                <button onClick={() => setManualMode(true)}
                  className="w-full py-4 rounded-2xl border border-gray-200 bg-gray-50 flex items-center justify-center gap-2 text-gray-600 font-semibold active:bg-gray-100">
                  ⌨️ Saisir manuellement
                </button>
              )}
            </div>

            {manualMode && (
              <div className="flex gap-2">
                <button onClick={() => setShowScanner(true)}
                  className="flex-1 py-3.5 border border-indigo-200 bg-indigo-50 text-indigo-700 rounded-2xl font-semibold flex items-center justify-center gap-2">
                  <Camera className="w-4 h-4" /> Scanner
                </button>
                <button
                  onClick={() => checkBarcode(form.barcode)}
                  disabled={checking || !form.barcode.trim()}
                  className="flex-1 py-3.5 bg-indigo-600 text-white rounded-2xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  {checking
                    ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : 'Vérifier →'}
                </button>
              </div>
            )}

            {checking && !manualMode && (
              <div className="text-center py-4">
                <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-gray-500 text-sm mt-2">Vérification...</p>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 1: INFO ── */}
        {step === 'info' && (
          <div className="space-y-4">
            {/* Barcode badge */}
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-2xl px-4 py-2.5">
              <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
              <span className="text-green-700 text-sm font-mono font-semibold flex-1">{form.barcode}</span>
              <button onClick={() => setStep('barcode')} className="text-gray-400 text-xs underline">Changer</button>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Nom *</label>
              <input autoFocus value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Ex: Réfrigérateur Condor 570L"
                className={`w-full px-4 py-3.5 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${errors.name ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Unité</label>
              <div className="flex gap-2">
                {['pièce', 'kg', 'litre', 'boîte'].map(u => (
                  <button key={u} onClick={() => set('unit', u)}
                    className={`flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 ${form.unit === u ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600'}`}>
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: PRIX ── */}
        {step === 'prix' && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
              <p className="text-amber-700 text-sm">💡 <strong>Ras mel</strong> = combien 5sertou. <strong>Prix min</strong> = moins que ça = alert!</p>
            </div>
            {[
              { key: 'ras_mel', label: 'Ras mel (Prix achat) *' },
              { key: 'prix_vente', label: 'Prix vente normal *' },
              { key: 'prix_min', label: 'Prix minimum (alerte) *' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">{label}</label>
                <div className="relative">
                  <input type="number" inputMode="decimal" value={(form as any)[key]}
                    onChange={e => set(key, e.target.value)} placeholder="0.000"
                    className={`w-full px-4 py-3.5 pr-12 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${(errors as any)[key] ? 'border-red-400' : 'border-gray-200'}`} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
                </div>
                {(errors as any)[key] && <p className="text-red-500 text-sm mt-1">{(errors as any)[key]}</p>}
              </div>
            ))}
            {form.ras_mel && form.prix_vente && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-3">
                <p className="text-green-700 text-sm">✅ Marge: <strong>+{(((parseFloat(form.prix_vente) - parseFloat(form.ras_mel)) / parseFloat(form.ras_mel)) * 100).toFixed(1)}%</strong></p>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3: STOCK ── */}
        {step === 'stock' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Stock actuel *</label>
              <input type="number" inputMode="numeric" autoFocus value={form.stock}
                onChange={e => set('stock', e.target.value)} placeholder="0"
                className={`w-full px-4 py-3.5 rounded-2xl border bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 ${errors.stock ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.stock && <p className="text-red-500 text-sm mt-1">{errors.stock}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Alert quand stock {'<'} X</label>
              <input type="number" inputMode="numeric" value={form.stock_alert}
                onChange={e => set('stock_alert', e.target.value)}
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500" />
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 space-y-1.5 text-sm">
              <h3 className="font-bold text-indigo-800 mb-2">Récap</h3>
              {[
                ['Barcode', form.barcode],
                ['Article', form.name],
                ['Ras mel', form.ras_mel + ' DT'],
                ['Prix vente', form.prix_vente + ' DT'],
                ['Prix min', form.prix_min + ' DT'],
                ['Stock', form.stock + ' ' + form.unit],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between">
                  <span className="text-gray-500">{l}</span>
                  <span className="font-semibold font-mono">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── DONE ── */}
        {step === 'done' && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1">Article ajouté! ✅</h2>
            <p className="text-gray-500 mb-1 font-semibold">{form.name}</p>
            <p className="text-gray-400 text-sm font-mono mb-8">{form.barcode}</p>
            <div className="flex flex-col gap-3">
              <button onClick={() => {
                setForm({ name: '', barcode: '', category_id: '', unit: 'pièce', ras_mel: '', prix_vente: '', prix_min: '', stock: '', stock_alert: '5' })
                setManualMode(false)
                setStep('barcode')
              }} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg">
                + Ajouter un autre
              </button>
              <Link href="/articles" className="w-full py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold text-lg text-center block">
                Voir les articles
              </Link>
            </div>
          </div>
        )}

        {/* Next button */}
        {(step === 'info' || step === 'prix' || step === 'stock') && (
          <button onClick={next} disabled={loading}
            className="w-full mt-8 py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
            {loading
              ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : step === 'stock' ? 'Enregistrer ✓' : 'Suivant →'}
          </button>
        )}
      </div>
    </div>
  )
}
